/**
 * Ad-event tracking for BrandedEmbeddablePlaylist.
 *
 * Posts `ad-impression` / `ad-click` events to the shared clip-views intake
 * (`/methods/track`) — same lambda that already tallies clip views, extended in
 * ALL-1015 to accept ad-event payloads. We piggyback on that pipeline so we
 * don't stand up a parallel ad-tracking service: the lambda tallies in Redis
 * and the writer (ALL-1016) flushes to `AdCampaignStats` on a cadence.
 *
 * Wire shape (matches the lambda intake schema):
 *   {
 *     type: 'campaign',
 *     id: <campaignId>,
 *     attr: 'impression' | 'click',
 *     slot,
 *     viewerGeo,
 *     sessionId
 *   }
 *
 * `creativeId` is intentionally NOT on the wire — slot+campaign uniquely
 * identifies the creative downstream via the campaign config. We still use
 * creativeId as part of the client-side dedup key so a campaign swap mid-page
 * (rare, but possible if config refetches) refires correctly.
 *
 * - Impression dedup is client-side via `useImpressionBeacon`'s useRef<Set>
 *   keyed by (campaignId, slot, creativeId, clipId). One impression per slot
 *   per clip play per page life — matches CMS-10 acceptance ("4 impression
 *   beacons fire per clip play").
 * - sessionId is in-memory only (per ALL-1019 V1 decision): one stable id per
 *   page load, regenerated on reload. Good enough for V1; if reload abuse
 *   becomes an issue we'd move this to localStorage.
 * - Transport is `fetch` with `keepalive: true` — fire-and-forget, survives
 *   page unload (so a CTA click that fires a beacon immediately before
 *   navigating still lands). We deliberately do NOT use `navigator.sendBeacon`:
 *   sendBeacon always sends with credentials mode `include` (cookies attached,
 *   no opt-out), which forces strict CORS — server must echo a specific
 *   `Access-Control-Allow-Origin` AND `Access-Control-Allow-Credentials: true`.
 *   Keepalive fetch defaults to `same-origin` credentials, which the track
 *   lambda's existing CORS config already handles cleanly.
 */
import { useCallback, useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";

import type { AdSlot } from "./@types";
import {
  BrandedActiveClipState,
  BrandedSponsorConfigState,
  BrandedViewerGeoState,
} from "./State";

const TRACK_ENDPOINT = "https://api.prod.allstar.dev/methods/track";

/**
 * The track lambda's writer step does `new Types.ObjectId(campaignId)` on
 * flush — non-hex ids crash the flush loop and bin every event in that
 * batch. Skip beacons for campaign ids that aren't valid Mongo ObjectIds
 * (e.g. the local "allstar-self-takeover" fallback). Real Ghostface-issued
 * campaigns always have 24-char hex ids.
 */
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
const isValidCampaignId = (id: string | undefined): id is string =>
  !!id && OBJECT_ID_RE.test(id);

/** Min visible fraction (0–1) before an impression is allowed to start dwelling. */
const VISIBILITY_THRESHOLD = 0.5;
/** Min continuous dwell at threshold before the beacon fires. */
const DWELL_MS = 500;

type AdEventAttr = "impression" | "click";

interface AdEventPayload {
  type: "campaign";
  id: string;
  attr: AdEventAttr;
  slot: AdSlot;
  viewerGeo: string;
  sessionId: string;
  /**
   * Active clip at fire time. Sent on impressions so the lambda can dedup
   * per (campaign, slot, geo, session, CLIP) rather than per-session — without
   * it, the server's 300s dedup window collapses every per-clip re-fire back
   * into one. Omitted on clicks (per-clip click dedup isn't meaningful).
   * NOTE: the track lambda's parser is a strict allow-list, so it must be
   * taught to accept `clipId` BEFORE this field ships — deploy lambda first.
   */
  clipId?: string;
}

/**
 * Page-life session id. Per ALL-1019 V1 decision: in-memory only, regenerated
 * on every page load. crypto.randomUUID is preferred but absent on some older
 * Safari versions still in the wild — fall back to a timestamp-suffixed random
 * id which is good enough to dedup at the lambda within a flush window.
 */
const generateSessionId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  return `s_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const SESSION_ID = generateSessionId();

/** Exposed for tests / debug; do not mutate. */
export const getSessionId = (): string => SESSION_ID;

/**
 * Post an event via keepalive fetch. Fire-and-forget — the request commits
 * to the browser's outgoing queue immediately and survives page unload, so a
 * CTA click that fires this synchronously right before navigating still
 * lands. We never await or surface errors: ad telemetry must never block UX
 * or throw into a React render path.
 *
 * `credentials` is left at its default (`same-origin`) so cross-origin
 * requests don't pull in cookies — keeps the CORS contract simple
 * (allow-origin only, no allow-credentials needed).
 */
const postEvent = (payload: AdEventPayload): void => {
  if (typeof fetch !== "function") return;
  try {
    void fetch(TRACK_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* swallow; tracking must never throw */
    });
  } catch {
    /* swallow; tracking must never throw */
  }
};

interface FireClickArgs {
  campaignId: string | undefined;
  slot: AdSlot;
  creativeId: string | undefined;
  viewerGeo: string;
}

/**
 * Fire an ad-click beacon. Called synchronously from a CTA `onClick` BEFORE
 * the native link navigation runs — sendBeacon doesn't block, so the click
 * still feels instant.
 *
 * Returns silently if `campaignId` or `creativeId` are missing (e.g. a slot
 * is rendered with placeholder data) — there's nothing meaningful to attribute
 * the click to. We intentionally don't error: missing IDs are a config bug,
 * not a runtime crash worthy of breaking the user's click.
 */
export const fireAdClickBeacon = ({
  campaignId,
  slot,
  creativeId,
  viewerGeo,
}: FireClickArgs): void => {
  if (!isValidCampaignId(campaignId) || !creativeId) return;
  postEvent({
    type: "campaign",
    id: campaignId,
    attr: "click",
    slot,
    viewerGeo,
    sessionId: SESSION_ID,
  });
};

interface UseImpressionBeaconArgs {
  slot: AdSlot;
  creativeId: string | undefined;
  /**
   * When false the observer is detached and no beacon can fire — used to
   * suspend tracking until a slot actually appears (e.g. the top banner only
   * counts as impressed once it slides in; an off-screen banner that never
   * animates in shouldn't tally).
   */
  enabled?: boolean;
}

/**
 * Returns a ref callback to attach to the slot's wrapping element. When the
 * element is ≥50% visible for ≥500ms continuously, fires one `ad-impression`
 * per (campaignId, slot, creativeId, clipId) per page life.
 *
 * The dedup key includes `clipId` so navigating to the next clip in the
 * playlist re-fires the same slot — exactly the "4 impressions per clip play"
 * behavior CMS-10 specifies.
 *
 * Reads campaignId / viewerGeo / clipId from Recoil at fire time (not at hook
 * setup) so a campaign that resolves after first render still tallies, and a
 * mid-playback clip change re-arms the dedup key for the new clip.
 */
export const useImpressionBeacon = ({
  slot,
  creativeId,
  enabled = true,
}: UseImpressionBeaconArgs): ((node: HTMLElement | null) => void) => {
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const viewerGeo = useRecoilValue(BrandedViewerGeoState);
  const activeClip = useRecoilValue(BrandedActiveClipState);
  const clipId = activeClip?._id ?? activeClip?.shareId ?? "no-clip";

  const campaignId = sponsor?.campaignId;

  // Per-session dedup ledger. A Set instead of an Object so cleanup
  // (clear-on-unmount) is a single .clear() call rather than tracking keys.
  const firedRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const dwellTimerRef = useRef<number | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  // Capture the latest values in a ref so the IO callback (which is created
  // once per observed element) always reads current state without us having
  // to tear down + rebuild the observer on every Recoil change.
  const latestRef = useRef({ campaignId, creativeId, viewerGeo, clipId, slot });
  latestRef.current = { campaignId, creativeId, viewerGeo, clipId, slot };

  const fireIfNew = useCallback(() => {
    const {
      campaignId: cId,
      creativeId: crId,
      viewerGeo: geo,
      clipId: cl,
      slot: s,
    } = latestRef.current;
    if (!isValidCampaignId(cId) || !crId) return;
    const key = `${cId}|${s}|${crId}|${cl}`;
    if (firedRef.current.has(key)) return;
    firedRef.current.add(key);
    postEvent({
      type: "campaign",
      id: cId,
      attr: "impression",
      slot: s,
      viewerGeo: geo,
      sessionId: SESSION_ID,
      clipId: cl,
    });
  }, []);

  const clearDwell = useCallback(() => {
    if (dwellTimerRef.current !== null) {
      window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearDwell();
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [clearDwell]);

  const attach = useCallback(
    (node: HTMLElement | null) => {
      // Detach from previous node first — ref callbacks fire with `null` on
      // unmount, and the React reconciler may also call with a fresh node
      // without an explicit `null` in between when the element identity changes.
      if (nodeRef.current && observerRef.current) {
        observerRef.current.unobserve(nodeRef.current);
      }
      nodeRef.current = node;

      if (!node || !enabled) {
        clearDwell();
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        // Older browsers without IO get a degraded "fire on mount" — better
        // to overcount slightly than to silently drop all impressions on
        // legacy Safari embeds.
        fireIfNew();
        return;
      }

      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            if (entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
              if (dwellTimerRef.current === null) {
                dwellTimerRef.current = window.setTimeout(() => {
                  dwellTimerRef.current = null;
                  fireIfNew();
                }, DWELL_MS);
              }
            } else {
              clearDwell();
            }
          },
          { threshold: [0, VISIBILITY_THRESHOLD, 1] },
        );
      }
      observerRef.current.observe(node);
    },
    [enabled, fireIfNew, clearDwell],
  );

  // Tear down on unmount.
  useEffect(() => teardown, [teardown]);

  // If `enabled` flips false (e.g. banner dismissed before dwell completed)
  // cancel the pending dwell timer so it doesn't fire stale.
  useEffect(() => {
    if (!enabled) clearDwell();
  }, [enabled, clearDwell]);

  // Re-arm on clip change. A new clip is a fresh impression opportunity (the
  // dedup key includes clipId), but the IntersectionObserver only invokes its
  // callback on a visibility *crossing*. For always-on slots — the upper-left
  // watermark and the top banner — the element never leaves the viewport when
  // the clip advances, so the observer never re-delivers an entry on its own
  // and the new clip would never fire. Re-`observe()` the same node to force a
  // fresh initial entry; the new clipId in the dedup key prevents this from
  // double-firing within a single clip. Slots that already leave + re-enter the
  // viewport per clip (the endcard, via its `enabled` toggle) are unaffected —
  // this just makes them re-arm one extra, harmless, deduped time.
  useEffect(() => {
    const obs = observerRef.current;
    const node = nodeRef.current;
    if (!enabled || !obs || !node) return;
    obs.unobserve(node);
    obs.observe(node);
  }, [clipId, enabled]);

  return attach;
};
